/**
 * 路線價格 /route-prices（20260611 反饋批次）
 * Tabs：路線價格（區對區 × 車款 × HKD）｜加購項目（舉牌服務 / 嬰兒座椅）。
 */
import { useMemo, useState } from "react";
import { Plus, Pencil, Power, PowerOff, ArrowRight, AlertTriangle } from "lucide-react";
import { trpc } from "../../lib/trpc";
import { useLanguage } from "../../contexts/LanguageContext";
import { Card, CardContent } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input, Select } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../../components/ui/dialog";
import { AIRPORTS, REGIONS, type Loc, type Region } from "../../../../shared/const";
import { ADDON_CODES, BOOKING_AIRPORT_CODES, ZONES, type AddonCode } from "../../../../shared/booking";
import { findRoutePriceGaps } from "../../../../shared/route-price-gaps";
import { ActiveBadge, REGION_LOC, THEAD_ROW, airportLoc, zoneLoc } from "./base-data-shared";

type EndpointType = "airport" | "zone";

type RouteRow = {
  id: number;
  region: Region;
  originType: EndpointType;
  originCode: string;
  destType: EndpointType;
  destCode: string;
  vehicleModelId: number;
  priceHkd: string;
  isActive: "yes" | "no";
};

type ModelRow = { id: number; name: string; region: Region | null; isActive: "yes" | "no" };

type AddonRow = { code: AddonCode; name: string; priceHkd: string };

const ADDON_LOC: Record<AddonCode, Loc> = {
  luggage_sign: { "zh-TW": "舉牌服務", "en-US": "Sign holding service", "zh-CN": "举牌服务" },
  child_seat: { "zh-TW": "嬰兒座椅", "en-US": "Infant seat", "zh-CN": "婴儿座椅" },
};

const endpointLoc = (type: EndpointType, code: string): Loc =>
  type === "airport" ? airportLoc(code) : zoneLoc(code);

const airportsFor = (r: Region) =>
  AIRPORTS.filter((a) => a.region === r && (BOOKING_AIRPORT_CODES as readonly string[]).includes(a.code));
const zonesFor = (r: Region) => ZONES.filter((z) => z.region === r);
const defaultCode = (type: EndpointType, r: Region) =>
  type === "airport" ? airportsFor(r)[0]!.code : zonesFor(r)[0]!.code;

export default function RoutePrices() {
  const { t } = useLanguage();
  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-display text-2xl font-medium tracking-tight">
          {t({ "zh-TW": "路線價格", "en-US": "Route Prices", "zh-CN": "路线价格" })}
        </h1>
        <p className="text-[13px] text-muted-foreground">
          {t({
            "zh-TW": "區對區（含機場）× 車款的售價，一律以 HKD 計價；需求方依其所在地幣別經匯率表換算顯示。",
            "en-US": "Zone-to-zone (incl. airports) × vehicle model prices in HKD; agencies see converted prices.",
            "zh-CN": "区对区（含机场）× 车款的售价，一律以 HKD 计价；需求方依其所在地币别经汇率表换算显示。",
          })}
        </p>
      </div>

      <Tabs defaultValue="routes">
        <TabsList>
          <TabsTrigger value="routes">{t({ "zh-TW": "路線價格", "en-US": "Routes", "zh-CN": "路线价格" })}</TabsTrigger>
          <TabsTrigger value="addons">{t({ "zh-TW": "加購項目", "en-US": "Add-ons", "zh-CN": "加购项目" })}</TabsTrigger>
        </TabsList>
        <TabsContent value="routes">
          <RoutesTab />
        </TabsContent>
        <TabsContent value="addons">
          <AddonsTab />
        </TabsContent>
      </Tabs>
    </div>
  );
}

/* ── 路線價格 tab ─────────────────────────────────────────────── */
function RoutesTab() {
  const { t } = useLanguage();
  const listQ = trpc.routePrice.list.useQuery();
  const modelsQ = trpc.vehicleModel.list.useQuery();
  const rows = (listQ.data ?? []) as RouteRow[];
  const models = (modelsQ.data ?? []) as ModelRow[];
  const modelName = (id: number) => models.find((m) => m.id === id)?.name ?? `#${id}`;

  const [fRegion, setFRegion] = useState<"all" | Region>("all");
  const [fModel, setFModel] = useState<"all" | number>("all");

  const setActive = trpc.routePrice.setActive.useMutation({ onSuccess: () => listQ.refetch() });

  const filtered = rows.filter(
    (r) => (fRegion === "all" || r.region === fRegion) && (fModel === "all" || r.vehicleModelId === fModel),
  );

  // 缺價提醒：不受上方篩選影響，一律看全部資料，否則篩選後會以為缺口消失了
  const gapGroups = useMemo(() => {
    const gaps = findRoutePriceGaps(rows, models);
    const byModel = new Map<string, { region: Region; modelName: string; routes: typeof gaps }>();
    for (const g of gaps) {
      const key = `${g.region}#${g.vehicleModelId}`;
      const hit = byModel.get(key);
      if (hit) hit.routes.push(g);
      else byModel.set(key, { region: g.region, modelName: g.modelName, routes: [g] });
    }
    return [...byModel.values()];
  }, [rows, models]);
  const gapCount = gapGroups.reduce((n, g) => n + g.routes.length, 0);

  return (
    <div className="space-y-4">
      {gapCount > 0 && (
        <div className="flex items-start gap-3 rounded-md border border-[color-mix(in_oklab,var(--color-warning)_45%,transparent)] bg-[color-mix(in_oklab,var(--color-warning)_14%,transparent)] p-4">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-[var(--color-warning)]" />
          <div className="min-w-0 space-y-2">
            <div>
              <p className="text-[13px] font-medium">
                {t({
                  "zh-TW": `有 ${gapCount} 條路線缺價，旅行社目前送不出這些單`,
                  "en-US": `${gapCount} route(s) have no price — agencies cannot submit these orders`,
                  "zh-CN": `有 ${gapCount} 条路线缺价，旅行社目前送不出这些单`,
                })}
              </p>
              <p className="text-[12px] text-muted-foreground">
                {t({
                  "zh-TW": "這些路線設過價，但目前啟用中的車款沒有可用的價格（含價格列被停用的情況）。補上價格後這則提醒會自動消失。",
                  "en-US": "These routes have been priced before, but no active vehicle model has a usable price (including prices that were disabled). The notice clears once prices are added.",
                  "zh-CN": "这些路线设过价，但目前启用中的车款没有可用的价格（含价格列被停用的情况）。补上价格后这则提醒会自动消失。",
                })}
              </p>
            </div>
            {gapGroups.map((g) => (
              <div key={`${g.region}#${g.modelName}`} className="text-[12px]">
                <p className="font-medium">
                  {t(REGION_LOC[g.region])} · {g.modelName}
                  <span className="ml-1.5 text-muted-foreground">
                    {t({
                      "zh-TW": `缺 ${g.routes.length} 條`,
                      "en-US": `${g.routes.length} missing`,
                      "zh-CN": `缺 ${g.routes.length} 条`,
                    })}
                  </span>
                </p>
                <ul className="mt-0.5 space-y-0.5 text-muted-foreground">
                  {g.routes.slice(0, 8).map((r, i) => (
                    <li key={i} className="inline-flex items-center gap-1 pr-3">
                      {t(endpointLoc(r.originType, r.originCode))}
                      <ArrowRight className="h-3 w-3" />
                      {t(endpointLoc(r.destType, r.destCode))}
                    </li>
                  ))}
                  {g.routes.length > 8 && (
                    <li>
                      {t({
                        "zh-TW": `⋯另 ${g.routes.length - 8} 條`,
                        "en-US": `…and ${g.routes.length - 8} more`,
                        "zh-CN": `⋯另 ${g.routes.length - 8} 条`,
                      })}
                    </li>
                  )}
                </ul>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap items-center gap-2">
          <Select className="w-36" value={fRegion} onChange={(e) => setFRegion(e.target.value as "all" | Region)}>
            <option value="all">{t({ "zh-TW": "全部區域", "en-US": "All regions", "zh-CN": "全部区域" })}</option>
            {REGIONS.map((r) => (
              <option key={r} value={r}>
                {t(REGION_LOC[r])}
              </option>
            ))}
          </Select>
          <Select
            className="w-36"
            value={String(fModel)}
            onChange={(e) => setFModel(e.target.value === "all" ? "all" : Number(e.target.value))}
          >
            <option value="all">{t({ "zh-TW": "全部車款", "en-US": "All models", "zh-CN": "全部车款" })}</option>
            {models.map((m) => (
              <option key={m.id} value={m.id}>
                {m.name}
              </option>
            ))}
          </Select>
        </div>
        <RouteDialog models={models} onDone={() => listQ.refetch()} />
      </div>

      <Card>
        <CardContent className="p-0">
          {listQ.isLoading || modelsQ.isLoading ? (
            <div className="h-24 animate-pulse rounded-lg bg-muted/40" />
          ) : filtered.length === 0 ? (
            <p className="p-8 text-center text-[13px] text-muted-foreground">
              {t({ "zh-TW": "查無符合條件的路線價格。", "en-US": "No route prices match the filters.", "zh-CN": "查无符合条件的路线价格。" })}
            </p>
          ) : (
            <table className="w-full text-[13px]">
              <thead>
                <tr className={THEAD_ROW}>
                  <th className="p-3">{t({ "zh-TW": "區域", "en-US": "Region", "zh-CN": "区域" })}</th>
                  <th className="p-3">{t({ "zh-TW": "路線", "en-US": "Route", "zh-CN": "路线" })}</th>
                  <th className="p-3">{t({ "zh-TW": "車款", "en-US": "Model", "zh-CN": "车款" })}</th>
                  <th className="p-3 text-right">{t({ "zh-TW": "價格 (HKD)", "en-US": "Price (HKD)", "zh-CN": "价格 (HKD)" })}</th>
                  <th className="p-3">{t({ "zh-TW": "狀態", "en-US": "Status", "zh-CN": "状态" })}</th>
                  <th className="p-3 text-right">{t({ "zh-TW": "操作", "en-US": "Action", "zh-CN": "操作" })}</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((r) => (
                  <tr key={r.id} className="border-b border-border/60 last:border-0">
                    <td className="p-3">{t(REGION_LOC[r.region])}</td>
                    <td className="p-3">
                      <span className="inline-flex items-center gap-1.5">
                        {t(endpointLoc(r.originType, r.originCode))}
                        <ArrowRight className="h-3.5 w-3.5 text-muted-foreground" />
                        {t(endpointLoc(r.destType, r.destCode))}
                      </span>
                    </td>
                    <td className="p-3">{modelName(r.vehicleModelId)}</td>
                    <td className="p-3 text-right numeric font-medium">{Number(r.priceHkd).toLocaleString()}</td>
                    <td className="p-3"><ActiveBadge active={r.isActive} /></td>
                    <td className="p-3 text-right whitespace-nowrap">
                      <RouteDialog route={r} models={models} onDone={() => listQ.refetch()} />
                      <Button
                        variant="ghost"
                        size="sm"
                        disabled={setActive.isPending}
                        onClick={() => setActive.mutate({ id: r.id, isActive: r.isActive === "yes" ? "no" : "yes" })}
                      >
                        {r.isActive === "yes" ? <PowerOff className="h-3.5 w-3.5" /> : <Power className="h-3.5 w-3.5" />}
                        {r.isActive === "yes"
                          ? t({ "zh-TW": "停用", "en-US": "Disable", "zh-CN": "停用" })
                          : t({ "zh-TW": "啟用", "en-US": "Enable", "zh-CN": "启用" })}
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function RouteDialog({ route, models, onDone }: { route?: RouteRow; models: ModelRow[]; onDone: () => void }) {
  const { t } = useLanguage();
  // 下拉只列啟用中車款（表格仍可顯示停用車款的既有列）
  const activeModels = models.filter((md) => md.isActive === "yes");
  const [open, setOpen] = useState(false);
  const [region, setRegion] = useState<Region>(route?.region ?? "taiwan");
  const [originType, setOriginType] = useState<EndpointType>(route?.originType ?? "airport");
  const [originCode, setOriginCode] = useState(route?.originCode ?? defaultCode("airport", "taiwan"));
  const [destType, setDestType] = useState<EndpointType>(route?.destType ?? "zone");
  const [destCode, setDestCode] = useState(route?.destCode ?? defaultCode("zone", "taiwan"));
  const [vehicleModelId, setVehicleModelId] = useState<number>(route?.vehicleModelId ?? activeModels[0]?.id ?? 0);
  const [priceHkd, setPriceHkd] = useState(route ? Number(route.priceHkd) : 0);

  const m = trpc.routePrice.upsert.useMutation({
    onSuccess: () => {
      setOpen(false);
      onDone();
    },
  });

  // 開啟時從 props/初始值重新同步草稿（避免殘留上一次新增/取消的輸入）；
  // 新增時順便補上預設車款（dialog 可能在 models 載入前掛載，初始 0）
  const changeOpen = (o: boolean) => {
    setOpen(o);
    if (o) {
      setRegion(route?.region ?? "taiwan");
      setOriginType(route?.originType ?? "airport");
      setOriginCode(route?.originCode ?? defaultCode("airport", "taiwan"));
      setDestType(route?.destType ?? "zone");
      setDestCode(route?.destCode ?? defaultCode("zone", "taiwan"));
      setVehicleModelId(route?.vehicleModelId ?? activeModels[0]?.id ?? 0);
      setPriceHkd(route ? Number(route.priceHkd) : 0);
    }
  };

  const changeRegion = (r: Region) => {
    setRegion(r);
    setOriginCode(defaultCode(originType, r));
    setDestCode(defaultCode(destType, r));
  };
  const changeOriginType = (ty: EndpointType) => {
    setOriginType(ty);
    setOriginCode(defaultCode(ty, region));
  };
  const changeDestType = (ty: EndpointType) => {
    setDestType(ty);
    setDestCode(defaultCode(ty, region));
  };

  const bothAirports = originType === "airport" && destType === "airport";
  const invalid = bothAirports || !Number.isFinite(priceHkd) || priceHkd <= 0 || vehicleModelId <= 0;

  const typeOptions = (
    <>
      <option value="airport">{t({ "zh-TW": "機場", "en-US": "Airport", "zh-CN": "机场" })}</option>
      <option value="zone">{t({ "zh-TW": "地區", "en-US": "Zone", "zh-CN": "地区" })}</option>
    </>
  );
  const codeOptions = (ty: EndpointType) =>
    ty === "airport"
      ? airportsFor(region).map((a) => (
          <option key={a.code} value={a.code}>
            {t(a.name)}
          </option>
        ))
      : zonesFor(region).map((z) => (
          <option key={z.code} value={z.code}>
            {t(z.label)}
          </option>
        ));

  const save = () =>
    m.mutate({ id: route?.id, region, originType, originCode, destType, destCode, vehicleModelId, priceHkd });

  return (
    <Dialog open={open} onOpenChange={changeOpen}>
      <DialogTrigger asChild>
        {route ? (
          <Button variant="ghost" size="sm">
            <Pencil className="h-3.5 w-3.5" />
            {t({ "zh-TW": "編輯", "en-US": "Edit", "zh-CN": "编辑" })}
          </Button>
        ) : (
          <Button size="sm">
            <Plus className="h-3.5 w-3.5" />
            {t({ "zh-TW": "新增路線價格", "en-US": "New route price", "zh-CN": "新增路线价格" })}
          </Button>
        )}
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {route
              ? t({ "zh-TW": "編輯路線價格", "en-US": "Edit route price", "zh-CN": "编辑路线价格" })
              : t({ "zh-TW": "新增路線價格", "en-US": "New route price", "zh-CN": "新增路线价格" })}
          </DialogTitle>
          <DialogDescription>
            {t({
              "zh-TW": "起迄點選項依區域連動；價格一律 HKD。",
              "en-US": "Endpoints follow the selected region; prices are in HKD.",
              "zh-CN": "起讫点选项依区域联动；价格一律 HKD。",
            })}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>{t({ "zh-TW": "區域", "en-US": "Region", "zh-CN": "区域" })}</Label>
              <Select value={region} onChange={(e) => changeRegion(e.target.value as Region)}>
                {REGIONS.map((r) => (
                  <option key={r} value={r}>
                    {t(REGION_LOC[r])}
                  </option>
                ))}
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>{t({ "zh-TW": "車款", "en-US": "Model", "zh-CN": "车款" })}</Label>
              <Select value={String(vehicleModelId)} onChange={(e) => setVehicleModelId(Number(e.target.value))}>
                {activeModels.map((md) => (
                  <option key={md.id} value={md.id}>
                    {md.name}
                  </option>
                ))}
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>{t({ "zh-TW": "起點類型", "en-US": "Origin type", "zh-CN": "起点类型" })}</Label>
              <Select value={originType} onChange={(e) => changeOriginType(e.target.value as EndpointType)}>
                {typeOptions}
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>{t({ "zh-TW": "起點", "en-US": "Origin", "zh-CN": "起点" })}</Label>
              <Select value={originCode} onChange={(e) => setOriginCode(e.target.value)}>
                {codeOptions(originType)}
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>{t({ "zh-TW": "迄點類型", "en-US": "Dest type", "zh-CN": "讫点类型" })}</Label>
              <Select value={destType} onChange={(e) => changeDestType(e.target.value as EndpointType)}>
                {typeOptions}
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>{t({ "zh-TW": "迄點", "en-US": "Destination", "zh-CN": "讫点" })}</Label>
              <Select value={destCode} onChange={(e) => setDestCode(e.target.value)}>
                {codeOptions(destType)}
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>{t({ "zh-TW": "價格", "en-US": "Price", "zh-CN": "价格" })}（HKD）</Label>
              <Input type="number" min={1} value={priceHkd} onChange={(e) => setPriceHkd(Number(e.target.value))} />
            </div>
          </div>
          {bothAirports && (
            <p className="text-[12px] text-destructive">
              {t({ "zh-TW": "不支援機場對機場路線。", "en-US": "Airport-to-airport routes are not supported.", "zh-CN": "不支持机场对机场路线。" })}
            </p>
          )}
          {m.error && <p className="text-[12px] text-destructive">{m.error.message}</p>}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => setOpen(false)}>
            {t({ "zh-TW": "取消", "en-US": "Cancel", "zh-CN": "取消" })}
          </Button>
          <Button onClick={save} disabled={m.isPending || invalid}>
            {t({ "zh-TW": "儲存", "en-US": "Save", "zh-CN": "保存" })}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ── 加購項目 tab（兩列固定：舉牌服務 / 嬰兒座椅）────────────── */
function AddonsTab() {
  const { t } = useLanguage();
  const listQ = trpc.addonService.list.useQuery();
  const rows = (listQ.data ?? []) as AddonRow[];
  const byCode = new Map(rows.map((r) => [r.code, r]));

  return (
    <Card>
      <CardContent className="p-0">
        {listQ.isLoading ? (
          <div className="h-24 animate-pulse rounded-lg bg-muted/40" />
        ) : (
          <table className="w-full text-[13px]">
            <thead>
              <tr className={THEAD_ROW}>
                <th className="p-3">{t({ "zh-TW": "項目", "en-US": "Item", "zh-CN": "项目" })}</th>
                <th className="p-3">{t({ "zh-TW": "顯示名稱", "en-US": "Display name", "zh-CN": "显示名称" })}</th>
                <th className="p-3 text-right">{t({ "zh-TW": "價格 (HKD)", "en-US": "Price (HKD)", "zh-CN": "价格 (HKD)" })}</th>
                <th className="p-3 text-right">{t({ "zh-TW": "操作", "en-US": "Action", "zh-CN": "操作" })}</th>
              </tr>
            </thead>
            <tbody>
              {ADDON_CODES.map((code) => {
                const r = byCode.get(code);
                return (
                  <tr key={code} className="border-b border-border/60 last:border-0">
                    <td className="p-3 font-medium">{t(ADDON_LOC[code])}</td>
                    <td className="p-3">{r?.name ?? <span className="text-muted-foreground/60">—</span>}</td>
                    <td className="p-3 text-right numeric font-medium">
                      {r ? Number(r.priceHkd).toLocaleString() : (
                        <span className="text-muted-foreground/60">{t({ "zh-TW": "未設定", "en-US": "unset", "zh-CN": "未设定" })}</span>
                      )}
                    </td>
                    <td className="p-3 text-right">
                      <AddonDialog code={code} existing={r} onDone={() => listQ.refetch()} />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </CardContent>
    </Card>
  );
}

function AddonDialog({ code, existing, onDone }: { code: AddonCode; existing?: AddonRow; onDone: () => void }) {
  const { t } = useLanguage();
  const [open, setOpen] = useState(false);
  const [name, setName] = useState(existing?.name ?? ADDON_LOC[code]["zh-TW"]);
  const [priceHkd, setPriceHkd] = useState(existing ? Number(existing.priceHkd) : 0);

  const m = trpc.addonService.upsert.useMutation({
    onSuccess: () => {
      setOpen(false);
      onDone();
    },
  });

  // 開啟時從 props/初始值重新同步草稿，避免殘留上一次取消的輸入
  const changeOpen = (o: boolean) => {
    setOpen(o);
    if (o) {
      setName(existing?.name ?? ADDON_LOC[code]["zh-TW"]);
      setPriceHkd(existing ? Number(existing.priceHkd) : 0);
    }
  };

  return (
    <Dialog open={open} onOpenChange={changeOpen}>
      <DialogTrigger asChild>
        {existing ? (
          <Button variant="ghost" size="sm">
            <Pencil className="h-3.5 w-3.5" />
            {t({ "zh-TW": "編輯", "en-US": "Edit", "zh-CN": "编辑" })}
          </Button>
        ) : (
          <Button size="sm" variant="outline">
            <Plus className="h-3.5 w-3.5" />
            {t({ "zh-TW": "設定", "en-US": "Set", "zh-CN": "设定" })}
          </Button>
        )}
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t(ADDON_LOC[code])}</DialogTitle>
          <DialogDescription>
            {t({
              "zh-TW": "下單時整列快照進訂單；價格一律 HKD。",
              "en-US": "Snapshotted into orders at booking time; price in HKD.",
              "zh-CN": "下单时整列快照进订单；价格一律 HKD。",
            })}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label>{t({ "zh-TW": "顯示名稱", "en-US": "Display name", "zh-CN": "显示名称" })}</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} />
          </div>
          <div className="space-y-1.5">
            <Label>{t({ "zh-TW": "價格", "en-US": "Price", "zh-CN": "价格" })}（HKD）</Label>
            <Input type="number" min={0} value={priceHkd} onChange={(e) => setPriceHkd(Number(e.target.value))} />
          </div>
          {m.error && <p className="text-[12px] text-destructive">{m.error.message}</p>}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => setOpen(false)}>
            {t({ "zh-TW": "取消", "en-US": "Cancel", "zh-CN": "取消" })}
          </Button>
          <Button
            onClick={() => m.mutate({ code, name: name.trim(), priceHkd })}
            disabled={m.isPending || name.trim() === "" || !Number.isFinite(priceHkd) || priceHkd < 0}
          >
            {t({ "zh-TW": "儲存", "en-US": "Save", "zh-CN": "保存" })}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
